import { useState } from 'react';
import {
  Container,
  Title,
  Text,
  Button,
  Table,
  Badge,
  Group,
  Stack,
  Card,
  Modal,
  TextInput,
  Textarea,
  Checkbox,
  ActionIcon,
  Code,
  CopyButton,
  Tooltip,
  Alert,
  Loader,
  Center,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { useForm } from '@mantine/form';
import { format } from 'date-fns';
import { formatDateDistance } from '../utils/date';
import {
  IconKey,
  IconPlus,
  IconTrash,
  IconCopy,
  IconCheck,
  IconAlertCircle,
} from '@tabler/icons-react';
import { api } from '@/services/api';

interface ApiKey {
  id: string;
  name: string;
  description?: string;
  key_prefix: string;
  scopes: string[];
  created_at: string;
  expires_at?: string;
  last_used_at?: string;
  is_active: boolean;
}

interface CreateApiKeyForm {
  name: string;
  description: string;
  expires_at: Date | null;
  scopes: string[];
}

export function ApiKeysPage() {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showKeyModal, setShowKeyModal] = useState(false);
  const [newlyCreatedKey, setNewlyCreatedKey] = useState<string | null>(null);
  const queryClient = useQueryClient();

  // Fetch API keys
  const { data: apiKeysData, isLoading: keysLoading } = useQuery({
    queryKey: ['apiKeys'],
    queryFn: async () => await api.getApiKeys(),
  });

  // Fetch available scopes
  const { data: scopesData, isLoading: scopesLoading } = useQuery({
    queryKey: ['apiKeyScopes'],
    queryFn: async () => await api.getApiKeyScopes(),
  });

  // Create API key mutation
  const createMutation = useMutation({
    mutationFn: async (data: CreateApiKeyForm) => {
      const payload = {
        name: data.name.trim(),
        description: data.description.trim() || undefined,
        expires_at: data.expires_at ? format(data.expires_at, 'yyyy-MM-dd') : undefined,
        scopes: data.scopes.length > 0 ? data.scopes : undefined,
      };
      return await api.createApiKey(payload);
    },
    onSuccess: (data: any) => {
      notifications.show({
        title: 'Success',
        message: 'API key created successfully',
        color: 'green',
      });
      setNewlyCreatedKey(data.key);
      setShowCreateModal(false);
      setShowKeyModal(true);
      queryClient.invalidateQueries({ queryKey: ['apiKeys'] });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to create API key',
        color: 'red',
      });
    },
  });

  // Delete API key mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => await api.deleteApiKey(id),
    onSuccess: () => {
      notifications.show({
        title: 'Success',
        message: 'API key deleted successfully',
        color: 'green',
      });
      queryClient.invalidateQueries({ queryKey: ['apiKeys'] });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to delete API key',
        color: 'red',
      });
    },
  });

  const form = useForm<CreateApiKeyForm>({
    initialValues: {
      name: '',
      description: '',
      expires_at: null,
      scopes: [],
    },
    validate: {
      name: (value) => (value.trim() ? null : 'Name is required'),
    },
  });

  const handleCreateSubmit = (values: CreateApiKeyForm) => {
    createMutation.mutate(values);
  };

  const handleDelete = (id: string, name: string) => {
    if (confirm(`Are you sure you want to delete API key "${name}"?`)) {
      deleteMutation.mutate(id);
    }
  };

  const handleSelectAll = () => {
    form.setFieldValue('scopes', scopesData?.scopes.map((s: any) => s.scope) || []);
  };

  const handleSelectDefault = () => {
    form.setFieldValue('scopes', scopesData?.default_scopes || []);
  };

  const handleClearScopes = () => {
    form.setFieldValue('scopes', []);
  };

  const apiKeys = apiKeysData?.api_keys || [];
  const isDevops = apiKeysData?.is_devops || false;

  if (keysLoading || scopesLoading) {
    return (
      <Container size="xl" py="xl">
        <Center h={400}>
          <Loader size="lg" />
        </Center>
      </Container>
    );
  }

  return (
    <Container size="xl" py="xl">
      {/* Header */}
      <Group justify="space-between" mb="xl">
        <div>
          <Group>
            <Title order={1}>API Keys</Title>
            {isDevops && (
              <Badge color="blue" variant="filled">
                DevOps Admin
              </Badge>
            )}
          </Group>
          <Text c="dimmed" mt="xs">
            Manage your API keys for accessing the pk8s API programmatically
          </Text>
        </div>
        <Button leftSection={<IconPlus size={16} />} onClick={() => setShowCreateModal(true)}>
          Create API Key
        </Button>
      </Group>

      {/* Empty State */}
      {apiKeys.length === 0 ? (
        <Card withBorder>
          <Stack align="center" py="xl">
            <IconKey size={48} stroke={1.5} color="gray" />
            <div style={{ textAlign: 'center' }}>
              <Text size="lg" fw={500} mb="xs">
                No API Keys Yet
              </Text>
              <Text c="dimmed" size="sm" mb="lg">
                Create your first API key to start using the pk8s API programmatically
              </Text>
            </div>
            <Button leftSection={<IconPlus size={16} />} onClick={() => setShowCreateModal(true)}>
              Create Your First API Key
            </Button>
          </Stack>
        </Card>
      ) : (
        <Card withBorder>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Name</Table.Th>
                <Table.Th>Key Prefix</Table.Th>
                <Table.Th>Scopes</Table.Th>
                <Table.Th>Created</Table.Th>
                <Table.Th>Expires</Table.Th>
                <Table.Th>Last Used</Table.Th>
                <Table.Th>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {apiKeys.map((key: ApiKey) => (
                <Table.Tr key={key.id}>
                  <Table.Td>
                    <div>
                      <Text fw={500}>{key.name}</Text>
                      {key.description && (
                        <Text size="xs" c="dimmed">
                          {key.description}
                        </Text>
                      )}
                    </div>
                  </Table.Td>
                  <Table.Td>
                    <Code>{key.key_prefix}</Code>
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs">
                      {key.scopes.length > 0 ? (
                        key.scopes.map((scope) => (
                          <Badge key={scope} size="sm" variant="light">
                            {scope}
                          </Badge>
                        ))
                      ) : (
                        <Text size="sm" c="dimmed">
                          No scopes
                        </Text>
                      )}
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">
                      {formatDateDistance(key.created_at)}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    {key.expires_at ? (
                      <Text size="sm">
                        {formatDateDistance(key.expires_at)}
                      </Text>
                    ) : (
                      <Text size="sm" c="dimmed">
                        Never
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    {key.last_used_at ? (
                      <Text size="sm">
                        {formatDateDistance(key.last_used_at)}
                      </Text>
                    ) : (
                      <Text size="sm" c="dimmed">
                        Never
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Tooltip label="Delete key">
                      <ActionIcon
                        color="red"
                        variant="subtle"
                        onClick={() => handleDelete(key.id, key.name)}
                        loading={deleteMutation.isPending}
                      >
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Tooltip>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Card>
      )}

      {/* Create API Key Modal */}
      <Modal
        opened={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          form.reset();
        }}
        title="Create API Key"
        size="lg"
      >
        <form onSubmit={form.onSubmit(handleCreateSubmit)}>
          <Stack gap="md">
            <TextInput
              label="Name"
              placeholder="My API Key"
              required
              {...form.getInputProps('name')}
            />

            <Textarea
              label="Description"
              placeholder="Optional description for this key"
              rows={3}
              {...form.getInputProps('description')}
            />

            <DatePickerInput
              label="Expires At"
              placeholder="Pick expiration date (optional)"
              clearable
              minDate={new Date()}
              {...form.getInputProps('expires_at')}
            />

            <div>
              <Group justify="space-between" mb="xs">
                <Text size="sm" fw={500}>
                  Scopes
                </Text>
                <Group gap="xs">
                  <Button size="xs" variant="subtle" onClick={handleSelectAll}>
                    Select All
                  </Button>
                  <Button size="xs" variant="subtle" onClick={handleSelectDefault}>
                    Default
                  </Button>
                  <Button size="xs" variant="subtle" onClick={handleClearScopes}>
                    Clear
                  </Button>
                </Group>
              </Group>
              <Stack gap="xs">
                {scopesData?.scopes.map((scope: any) => (
                  <Checkbox
                    key={scope.scope}
                    label={
                      <div>
                        <Text size="sm" fw={500}>
                          {scope.scope}
                        </Text>
                        {scope.description && (
                          <Text size="xs" c="dimmed">
                            {scope.description}
                          </Text>
                        )}
                      </div>
                    }
                    checked={form.values.scopes.includes(scope.scope)}
                    onChange={(e) => {
                      const checked = e.currentTarget.checked;
                      if (checked) {
                        form.setFieldValue('scopes', [...form.values.scopes, scope.scope]);
                      } else {
                        form.setFieldValue(
                          'scopes',
                          form.values.scopes.filter((s) => s !== scope.scope)
                        );
                      }
                    }}
                  />
                ))}
              </Stack>
            </div>

            <Group justify="flex-end" mt="md">
              <Button
                variant="subtle"
                onClick={() => {
                  setShowCreateModal(false);
                  form.reset();
                }}
              >
                Cancel
              </Button>
              <Button type="submit" loading={createMutation.isPending}>
                Create API Key
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>

      {/* Display New Key Modal */}
      <Modal
        opened={showKeyModal}
        onClose={() => {
          setShowKeyModal(false);
          setNewlyCreatedKey(null);
        }}
        title="API Key Created"
        closeOnClickOutside={false}
        closeOnEscape={false}
      >
        <Stack gap="md">
          <Alert color="yellow" icon={<IconAlertCircle size={16} />}>
            <Text size="sm" fw={500}>
              Save this key securely!
            </Text>
            <Text size="sm">
              This is the only time you will see this key. Store it in a safe place.
            </Text>
          </Alert>

          <div>
            <Text size="sm" fw={500} mb="xs">
              Your API Key:
            </Text>
            <Group gap="xs">
              <Code
                style={{
                  flex: 1,
                  padding: '8px',
                  wordBreak: 'break-all',
                }}
              >
                {newlyCreatedKey}
              </Code>
              <CopyButton value={newlyCreatedKey || ''}>
                {({ copied, copy }) => (
                  <Tooltip label={copied ? 'Copied' : 'Copy'}>
                    <ActionIcon
                      color={copied ? 'green' : 'gray'}
                      variant="filled"
                      onClick={copy}
                    >
                      {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                    </ActionIcon>
                  </Tooltip>
                )}
              </CopyButton>
            </Group>
          </div>

          <Button
            fullWidth
            onClick={() => {
              setShowKeyModal(false);
              setNewlyCreatedKey(null);
            }}
          >
            I've Saved My Key
          </Button>
        </Stack>
      </Modal>
    </Container>
  );
}

